import { useState } from 'react';
import { useSongs } from '@/context/SongContext';
import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { ArrowRight, Copy, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

export default function MoveFragmentDialog({ 
    open, 
    onOpenChange, 
    fragmentId, 
    currentSongId,
    fragmentLabel 
}) {
    const { songs, moveFragment, copyFragment } = useSongs();
    const [loading, setLoading] = useState(false);
    const [action, setAction] = useState(null); // 'move' or 'copy'
    
    // Filter out current song from options
    const otherSongs = songs.filter(s => s.id !== currentSongId);

    const handleAction = async (targetSongId, actionType) => {
        setLoading(true);
        setAction(actionType);
        
        try {
            const success = actionType === 'move' 
                ? await moveFragment(currentSongId, fragmentId, targetSongId)
                : await copyFragment(currentSongId, fragmentId, targetSongId);
            
            if (success) {
                const targetSong = songs.find(s => s.id === targetSongId);
                toast.success(
                    actionType === 'move' 
                        ? `Moved to "${targetSong?.title || 'Untitled'}"` 
                        : `Copied to "${targetSong?.title || 'Untitled'}"`
                );
                onOpenChange(false);
            } else {
                toast.error('Something went wrong');
            }
        } catch (error) {
            console.error('Error:', error);
            toast.error('Failed to complete action');
        } finally {
            setLoading(false);
            setAction(null);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-sm">
                <DialogHeader>
                    <DialogTitle className="text-base">
                        Move or Copy Fragment
                    </DialogTitle>
                    <DialogDescription className="text-sm">
                        {fragmentLabel ? `"${fragmentLabel}"` : 'This fragment'} → another song
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-2 max-h-[300px] overflow-y-auto py-2">
                    {otherSongs.length === 0 ? (
                        <p className="text-sm text-muted-foreground text-center py-4">
                            No other songs to move to. Create another song first.
                        </p>
                    ) : (
                        otherSongs.map((song) => (
                            <div 
                                key={song.id}
                                className="flex items-center justify-between gap-2 p-2 rounded-md bg-muted/30 hover:bg-muted/50 transition-colors"
                            >
                                <span className="text-sm font-medium truncate flex-1">
                                    {song.title || 'Untitled'}
                                </span>
                                <div className="flex gap-1 shrink-0">
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => handleAction(song.id, 'move')}
                                        disabled={loading}
                                        className="h-7 px-2 text-xs"
                                    >
                                        {loading && action === 'move' ? (
                                            <Loader2 className="w-3 h-3 animate-spin" />
                                        ) : (
                                            <>
                                                <ArrowRight className="w-3 h-3 mr-1" />
                                                Move
                                            </>
                                        )}
                                    </Button>
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => handleAction(song.id, 'copy')}
                                        disabled={loading}
                                        className="h-7 px-2 text-xs"
                                    >
                                        {loading && action === 'copy' ? (
                                            <Loader2 className="w-3 h-3 animate-spin" />
                                        ) : (
                                            <>
                                                <Copy className="w-3 h-3 mr-1" />
                                                Copy
                                            </>
                                        )}
                                    </Button>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
}
